<template>
	<div v-if="variant" class="flex flex-col items-start">
		<div
			v-if="prices.price"
			class=" font-bold whitespace-nowrap leading-[1.1]"
			:class="[
				showMainPriceAsSale && 'text-price-sale',
				large ? 'typo-h1' : 'typo-h2',
			]"
			v-html="formatPriceWithSuperscript(prices.price)"
		/>
		<PriceBoxSale
			v-if="showClubPrice && prices.clubPrice"
			:price="prices.clubPrice"
		/>
	</div>
</template>

<script setup lang="ts">
	import type {ProductInfo} from '@globus/image-overlay';
	import {formatPriceWithSuperscript} from '@/utils';
	import {isValidEan} from '~/utils/ean';

	enum LeafletPricingVariant {
		C7 = 'C7',
		C5_C6 = 'C5_C6',
		C1_C10 = 'C1_C10',
		C2_C3 = 'C2_C3',
		C8_C9 = 'C8_C9',
		C4_C11 = 'C4_C11',
	}

	const CLUB_PRICE_VARIANTS = new Set<LeafletPricingVariant>([
		LeafletPricingVariant.C8_C9,
		LeafletPricingVariant.C2_C3,
		LeafletPricingVariant.C4_C11,
	]);

	type Props = {
		productInfo: ProductInfo;
		large?: boolean;
	};

	const props = defineProps<Props>();

	function getNumber(key: string): number | undefined {
		const value = props.productInfo[key];
		if (typeof value === 'number' && !Number.isNaN(value)) {
			return value;
		}
		if (typeof value === 'string' && value !== '' && !Number.isNaN(Number(value))) {
			return Number(value);
		}
	}

	const prices = computed(() => ({
		price: getNumber('price'),
		clubDiscount: getNumber('clubDiscount'),
		originalPrice: getNumber('originalPrice'),
		discount: getNumber('discount'),
		clubPrice: getNumber('clubPrice'),
	}));

	const variant = computed((): LeafletPricingVariant | null => {
		const {price, clubDiscount, originalPrice, discount, clubPrice} = prices.value;

		switch (true) {
			case clubPrice != null && price != null && originalPrice != null && discount != null && clubDiscount != null:
				return LeafletPricingVariant.C8_C9;

			case clubPrice != null && price != null:
				return LeafletPricingVariant.C2_C3;

			case clubPrice != null && (originalPrice != null || price != null):
				return LeafletPricingVariant.C4_C11;

			case price != null && originalPrice != null && discount != null:
				return LeafletPricingVariant.C1_C10;

			case price != null && isValidEan(props.productInfo.EAN):
				return LeafletPricingVariant.C5_C6;

			case price != null:
				return LeafletPricingVariant.C7;

			default:
				return null;
		}
	});

	const showClubPrice = computed(() => variant.value != null && CLUB_PRICE_VARIANTS.has(variant.value));

	const showMainPriceAsSale = computed(() => variant.value !== LeafletPricingVariant.C4_C11);
</script>
